import { fetchAuthSession } from 'aws-amplify/auth';
import { 
  GrammarFeature, 
  GrammarSearchFilters, 
  GrammarSearchResponse,
  GrammarImportData,
  GrammarExportOptions
} from '../types/grammar';

// Type definitions
export interface GrammarCategory {
  PK: string;
  SK: string;
  name: string;
  description?: string;
  featureCount?: number;
}

export interface GrammarSearchParams {
  category?: string;
  feature_id?: string;
  cefr?: string;
  complexity_min?: number;
  complexity_max?: number;
  limit?: number;
}

const getBaseUrl = () => {
  return process.env.NODE_ENV === 'development' 
    ? '/Prod' 
    : (process.env.REACT_APP_API_BASE_URL || 'https://p811zb8knk.execute-api.us-east-1.amazonaws.com/Prod');
};

const getAuthHeaders = async () => {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`
  } as const;
};

export const grammarService = {
  /**
   * Get all categories with their metadata
   */
  getCategories: async (): Promise<any[]> => {
    console.log('Fetching grammar categories...');
    const baseUrl = getBaseUrl();
    
    try {
      const response = await fetch(`${baseUrl}/secure/grammar-categories`, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', errorText);
        throw new Error(`Failed to fetch grammar categories: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('Categories response:', data);
      if (data.categories && data.categories.length > 0) {
        console.log('First category details:', JSON.stringify(data.categories[0], null, 2));
      }
      return data.categories || [];
    } catch (error) {
      console.error('Error fetching grammar categories:', error);
      throw error;
    }
  },

  /**
   * Get grammar features with optional filters
   */
  getFeatures: async (params: GrammarSearchParams = {}): Promise<{ features: GrammarFeature[], count: number, hasMore: boolean }> => {
    console.log('Fetching grammar features with params:', params);
    const baseUrl = getBaseUrl();
    
    try {
      const queryParams = new URLSearchParams();
      
      if (params.category) queryParams.append('category', params.category);
      if (params.feature_id) queryParams.append('feature_id', params.feature_id);
      if (params.cefr) queryParams.append('cefr', params.cefr);
      if (params.complexity_min !== undefined) queryParams.append('complexity_min', params.complexity_min.toString());
      if (params.complexity_max !== undefined) queryParams.append('complexity_max', params.complexity_max.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      
      const url = `${baseUrl}/secure/grammar-features${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      console.log('Fetching from URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', errorText);
        throw new Error(`Failed to fetch grammar features: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('Features response:', data);
      
      // Handle both single feature response and list response
      if (params.feature_id && !data.features) {
        return { features: [data], count: 1, hasMore: false };
      }
      
      return {
        features: data.features || [],
        count: data.count || 0,
        hasMore: data.hasMore || false
      };
    } catch (error) {
      console.error('Error fetching grammar features:', error);
      throw error;
    }
  },

  /**
   * Get a specific grammar feature by ID
   */
  getFeatureById: async (featureId: string): Promise<GrammarFeature> => {
    console.log('Fetching grammar feature:', featureId);
    const result = await grammarService.getFeatures({ feature_id: featureId });
    
    if (result.features.length === 0) {
      throw new Error(`Grammar feature ${featureId} not found`);
    }
    
    return result.features[0];
  },

  /**
   * Get grammar features by category
   */
  getFeaturesByCategory: async (category: string, limit?: number): Promise<{ features: GrammarFeature[], count: number, hasMore: boolean }> => {
    console.log('Fetching grammar features for category:', category);
    return grammarService.getFeatures({ category, limit });
  },

  async searchFeatures(filters: GrammarSearchFilters): Promise<GrammarSearchResponse> {
    const queryParams = new URLSearchParams();
    
    if (filters.searchTerm) queryParams.append('search', filters.searchTerm);
    if (filters.categories?.length) queryParams.append('categories', filters.categories.join(','));
    if (filters.cefrLevels?.length) queryParams.append('cefrLevels', filters.cefrLevels.join(','));
    if (filters.featureTypes?.length) queryParams.append('featureTypes', filters.featureTypes.join(','));
    if (filters.businessRelevance?.length) queryParams.append('businessRelevance', filters.businessRelevance.join(','));
    if (filters.tags?.length) queryParams.append('tags', filters.tags.join(','));
    if (filters.complexityRange) {
      queryParams.append('complexityMin', filters.complexityRange.min.toString());
      queryParams.append('complexityMax', filters.complexityRange.max.toString());
    }
    
    const baseUrl = getBaseUrl();
    const response = await fetch(`${baseUrl}/secure/grammar-features?${queryParams}`, {
      headers: await getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to search grammar features');
    }
    
    return response.json();
  },

  async getFeature(featureId: string): Promise<GrammarFeature> {
    const baseUrl = getBaseUrl();
    const response = await fetch(`${baseUrl}/secure/grammar-features/${featureId}`, {
      headers: await getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to get grammar feature');
    }
    
    return response.json();
  },

  async createFeature(feature: Partial<GrammarFeature>): Promise<GrammarFeature> {
    const baseUrl = getBaseUrl();
    const response = await fetch(`${baseUrl}/secure/grammar-features`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(feature)
    });
    
    if (!response.ok) {
      throw new Error('Failed to create grammar feature');
    }
    
    return response.json();
  },

  async updateFeature(featureId: string, feature: Partial<GrammarFeature>): Promise<GrammarFeature> {
    const baseUrl = getBaseUrl();
    const response = await fetch(`${baseUrl}/secure/grammar-features/${featureId}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(feature)
    });
    
    if (!response.ok) {
      throw new Error('Failed to update grammar feature');
    }
    
    return response.json();
  },

  /**
   * Delete a grammar feature by ID
   */
  deleteFeature: async (featureId: string): Promise<void> => {
    console.log('Deleting grammar feature:', featureId);
    const baseUrl = getBaseUrl(); // Use same pattern as other working DELETE endpoints
    const url = `${baseUrl}/secure/grammar-features/${featureId}`;
    
    console.log('Delete request details:', {
      featureId,
      baseUrl,
      fullUrl: url,
      method: 'DELETE',
      headers: await getAuthHeaders(),
      nodeEnv: process.env.NODE_ENV,
      apiKey: process.env.REACT_APP_API_KEY ? 'Present' : 'Missing'
    });
    
    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });

      console.log('Delete response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete API error:', errorText);
        throw new Error(`Failed to delete grammar feature: ${errorText || response.statusText}`);
      }

      console.log('Feature deleted successfully:', featureId);
    } catch (error) {
      console.error('Error deleting grammar feature:', error);
      throw error;
    }
  },

  /**
   * Import grammar features in bulk
   */
  importFeatures: async (data: GrammarImportData): Promise<{ 
    categories: number; 
    features: number; 
    relationships: number;
    errors: string[] 
  }> => {
    console.log('Importing grammar features:', data);
    const baseUrl = getBaseUrl();
    
    try {
      const response = await fetch(`${baseUrl}/secure/grammar-features/import`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Import API error:', errorText);
        throw new Error(`Failed to import grammar features: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('Import result:', result);
      return result;
    } catch (error) {
      console.error('Error importing grammar features:', error);
      throw error;
    }
  },

  async exportFeatures(options: GrammarExportOptions): Promise<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('format', options.format);
    if (options.includeMetadata) queryParams.append('includeMetadata', 'true');
    if (options.includeRelationships) queryParams.append('includeRelationships', 'true');
    if (options.includeAssessment) queryParams.append('includeAssessment', 'true');
    if (options.featureIds?.length) queryParams.append('featureIds', options.featureIds.join(','));
    
    const baseUrl = getBaseUrl();
    const response = await fetch(`${baseUrl}/secure/grammar-features/export?${queryParams}`, {
      headers: await getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to export grammar features');
    }
    
    return response.blob();
  },

  async generateMetadata(featureId: string): Promise<GrammarFeature> {
    const baseUrl = getBaseUrl();
    const response = await fetch(`${baseUrl}/secure/grammar-features/${featureId}/generate-metadata`, {
      method: 'POST',
      headers: await getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to generate metadata');
    }
    
    return response.json();
  },

  async getRelatedFeatures(featureId: string): Promise<GrammarFeature[]> {
    const baseUrl = getBaseUrl();
    const response = await fetch(`${baseUrl}/secure/grammar-features/${featureId}/related`, {
      headers: await getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to get related features');
    }
    
    return response.json();
  },

  /**
   * Get grammar analytics
   */
  getAnalytics: async (): Promise<any> => {
    console.log('Fetching grammar analytics...');
    const baseUrl = getBaseUrl();
    
    try {
      const response = await fetch(`${baseUrl}/secure/grammar-analytics`, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', errorText);
        throw new Error(`Failed to fetch grammar analytics: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('Analytics response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching grammar analytics:', error);
      throw error;
    }
  },

  /**
   * Get graph data for all features and their relationships
   */
  getGraphData: async (): Promise<any> => {
    console.log('Fetching grammar graph data...');
    const baseUrl = getBaseUrl();
    
    try {
      const response = await fetch(`${baseUrl}/secure/grammar-features/relationships`, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', errorText);
        throw new Error(`Failed to fetch grammar graph data: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('Graph data response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching grammar graph data:', error);
      throw error;
    }
  },


  /**
   * Get category metadata
   */
  getCategoryMetadata: async (categoryName: string): Promise<any> => {
    console.log('Fetching grammar category metadata:', categoryName);
    const baseUrl = getBaseUrl();
    
    try {
      const response = await fetch(`${baseUrl}/secure/grammar-categories/${categoryName}`, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', errorText);
        throw new Error(`Failed to fetch category metadata: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('Category metadata response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching category metadata:', error);
      throw error;
    }
  },

  /**
   * Create a new category
   */
  createCategory: async (categoryData: {
    name: string;
    display_name?: string;
    description?: string;
    linguistic_type?: string;
    complexity_range?: any;
    cefr_coverage?: any;
    business_relevance?: any;
    teaching_approach?: any;
    common_challenges?: any[];
  }): Promise<any> => {
    console.log('Creating grammar category:', categoryData);
    const baseUrl = getBaseUrl();
    
    try {
      const response = await fetch(`${baseUrl}/secure/grammar-categories`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(categoryData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', errorText);
        throw new Error(`Failed to create category: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('Create category response:', data);
      return data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  /**
   * Update category metadata
   */
  updateCategoryMetadata: async (categoryName: string, metadata: any): Promise<any> => {
    console.log('Updating grammar category metadata:', categoryName, metadata);
    const baseUrl = getBaseUrl();
    
    try {
      const response = await fetch(`${baseUrl}/secure/grammar-categories/${categoryName}`, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(metadata)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', errorText);
        throw new Error(`Failed to update category metadata: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('Update category metadata response:', data);
      return data;
    } catch (error) {
      console.error('Error updating category metadata:', error);
      throw error;
    }
  },

  /**
   * Delete a category (only if it has no features)
   */
  deleteCategory: async (categoryName: string): Promise<void> => {
    console.log('Deleting grammar category:', categoryName);
    const baseUrl = getBaseUrl();
    
    try {
      const response = await fetch(`${baseUrl}/secure/grammar-categories/${categoryName}`, {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', errorText);
        throw new Error(`Failed to delete category: ${errorText || response.statusText}`);
      }

      console.log('Category deleted successfully');
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }
};